<script lang="ts">
  import { onMount } from 'svelte';
  import { PUBLIC_API_URL } from '$env/static/public';
  import { afterNavigate, goto } from '$app/navigation';
  import { page } from '$app/stores';

  import type { ArtAssetsReadManyResponseBody } from '@do/shop-api';

  import { is } from '@shared/lib/utils';
  import { cn } from '@shared/lib';
  import { Container } from '@shared/ui/container';
  import { checkTitleComplexity, Heading } from '@shared/ui/heading';
  import { Pagination } from '@features/pagination';
  import type { Configurations } from '@entities/configurations';
  import { ArtworksSort } from '@features/artwork/artworks-sort';
  import { GridToggler } from '@widgets/grid-toggler';
  import { ArtworkList } from '@widgets/artwork/artwork-list';

  import type { ArtAssetsList } from '../types';

  export let configurations: Configurations;
  export let artworks: ArtAssetsList;
  export let pageNumber: number;
  export let loadMore: number;
  export let totalCount: number;

  $: isTitleComplex = checkTitleComplexity(configurations.name);
  $: title = `Artworks of <span class="${cn('text-balance', { 'md:text-nowrap': !isTitleComplex })}">${configurations.name}</span>`;

  let loading = false;
  let isMounted = false;
  let container: HTMLElement;

  let defaultParams = {
    column: 'year',
    order: 'desc',
  };

  let prevPage = pageNumber;

  let sortField = $page.url.searchParams.get('column') || defaultParams.column;
  let sortOrder = $page.url.searchParams.get('order') || defaultParams.order;

  const limit = 10;
  const dataKey = 'art_assets';

  onMount(() => {
    // @TODO - if svelte lifecycle methods updates in future releases and not triggering reactive variable on mount - remove this flag
    isMounted = true;
  });

  afterNavigate(({ to }) => {
    if (to?.url.searchParams?.size === 0) {
      // if we go by link to the same page with no query params - we need to reset sort params
      sortField = defaultParams.column;
      sortOrder = defaultParams.order;
    }
  });

  const getArtworks = async (params: { page: number; loadMore?: number }) => {
    const pageParams = {
      offset: `${limit * (params.page + (params.loadMore ?? 0) - 1)}`,
      limit: `${limit}`,
      column: sortField,
      order: sortOrder,
      institutions: String(configurations.id),
    };

    const searchParams = new URLSearchParams(pageParams).toString();
    const resp = await fetch(`${PUBLIC_API_URL}/art-assets?${searchParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const newPage = params?.page || 1;
    const isSamePage = params.page === prevPage;

    $page.url.searchParams.set('order', `${pageParams.order}`);
    $page.url.searchParams.set('column', `${pageParams.column}`);

    if (isSamePage) {
      if (params.loadMore) {
        $page.url.searchParams.set('loadMore', `${params.loadMore}`);
      } else {
        $page.url.searchParams.delete('loadMore');
      }

      goto(`?${$page.url.searchParams.toString()}`, {
        noScroll: isSamePage,
        replaceState: true,
        invalidateAll: false,
      });
    } else {
      $page.url.searchParams.delete('loadMore');

      const pageUrl = $page.route.id?.split('/').slice(0, -1).join('/');

      const newUrl = `${is(newPage !== 1, `${pageUrl}/page-${newPage}`, pageUrl)}?${$page.url.searchParams.toString()}`;

      await goto(newUrl, { replaceState: true, invalidateAll: false, noScroll: true });

      prevPage = pageNumber;
    }

    const res: ArtAssetsReadManyResponseBody = await resp.json();

    res.art_assets = res.art_assets?.filter((item) => item.main_photo);

    return res;
  };

  const applySort = async (field: string, order: string) => {
    if (!isMounted || !field || !order) return;

    const someReceivedArtworks = await getArtworks({ page: 1, loadMore: 0 });

    artworks = someReceivedArtworks?.[dataKey];
    loadMore = 0;
  };

  $: applySort(sortField, sortOrder);
</script>

<div bind:this="{container}">
  <Heading
    {title}
    tag="h1"
    counter="{totalCount}"
    titleClass="{cn('inline text-balance lg:text-4xl xl:text-5xl', { 'break-all': isTitleComplex })}"
    wrapperClass="flex flex-col items-start gap-10 lg:items-end lg:gap-12 lg:flex-row normal-case"
  >
    <div class="flex items-end self-center lg:self-end">
      <div class="w-[168px]">
        <ArtworksSort bind:field="{sortField}" bind:order="{sortOrder}" />
      </div>

      <GridToggler />
    </div>
  </Heading>
</div>

<Container containerClass="py-15">
  <Pagination
    containerToScroll="{container}"
    pageSize="{limit}"
    requestFunction="{getArtworks}"
    fetchedDataKey="{dataKey}"
    bind:currentPage="{pageNumber}"
    bind:loadMore
    bind:data="{artworks}"
    bind:totalDataCount="{totalCount}"
    bind:loading
  >
    <!-- <ArtworkList {artworks} /> -->
    <ArtworkList {artworks} />
  </Pagination>
</Container>
